import { useEffect } from 'react';
// The `/urql` subpath: the client the Agent Manager queries live on, as in
// `load-bot-versions.ts`, which reads the same history at load.
import { proStudioUrqlGql as agentManagerGql } from '@pypestream/api-services/urql';

import type { DeploymentLike } from '../utils/derive-deployments';
import { selectOpenSolutionId } from '../utils/select-open-solution-id';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../xstate/app.xstate';
import { reportObserverFailure } from '../xstate/report-observer-failure';

/**
 * Keeps the Versions table's history current while the page is open (PSTUDIO-4354).
 *
 * The table is built by `loadBotVersions` once per solution load, and before this was
 * refreshed only by `reloadCurrentBotData` — after a SUCCESSFUL publish, and only in the
 * tab that watched it. Nothing moved while a publish ran, after one failed, or for a
 * coworker. `ctx.solutionDeployments` (the live subscription, `useSolutionDeployments`)
 * already sees every attempt and every step in every tab, so its `id:status` pairs are the
 * trigger: when one appears or changes, the history is re-read.
 *
 * The history query, not the subscription rows, feeds the table: the subscription carries
 * the latest 20 attempts and `deployedBy` as a bare id, while the table lists every publish
 * with the deploying user's name and avatar.
 */
export function useLiveDeploymentHistory(): void {
  const solutionId = useProStudioCtxSelector(selectOpenSolutionId);
  // A string, so a re-send of the same attempts (a newer `updatedAt`, a fresh array) does
  // not count as a change.
  const signature = useProStudioCtxSelector((ctx) =>
    (ctx.solutionDeployments ?? [])
      .map(({ id, status }) => `${id}:${status}`)
      .sort()
      .join('|')
  );
  // Written by `loadBotVersions` when it lands (and cleared by `reloadCurrentBotData`). The
  // loader reads this same history, then makes several more round trips before it writes
  // `ctx.versions` — so it can overwrite a newer read with its older snapshot. Reading again
  // each time a load lands makes the live read always the last write. Until then the
  // machine drops the result anyway (`applyDeploymentHistory`), so do not read at all.
  const loadedSolutionId = useProStudioCtxSelector(
    (ctx) => ctx.selectedBot?.uniqueId
  );

  useEffect(() => {
    if (!solutionId || !signature || loadedSolutionId !== solutionId) {
      return undefined;
    }

    let current = true;

    const read = async () => {
      try {
        const { data, error } =
          await agentManagerGql.getSolutionDeploymentHistory({ solutionId });
        if (!current) return;
        if (error) throw error;
        const deployments =
          data?.admin_?.solutionById?.solutionDeployments?.filter(Boolean);
        if (!deployments) return;
        sendProStudioEvent({
          type: 'setDeploymentHistory',
          solutionId,
          deployments: deployments as DeploymentLike[],
        });
      } catch (error) {
        // The table keeps what it shows; the next status change reads again.
        if (current)
          reportObserverFailure(error, 'versions:deployment-history');
      }
    };
    read();

    return () => {
      // A newer read (or another solution) supersedes this one.
      current = false;
    };
  }, [solutionId, signature, loadedSolutionId]);
}
